// Brief-Vorlage anlegen/bearbeiten (dokument_vorlagen): Name, Typ, Betreff, Text; Platzhalter per Klick an der
// Cursorposition einfügen. Gespeichert wird schlichtes HTML (<h2>Betreff</h2><p>…</p>) – genau das, was
// Blanko-Brief und „Per Post senden“ über vorlageZuBrief wieder in Betreff + Text zerlegen.
import { useEffect, useRef, useState } from 'react';
import { FloppyDisk, PencilSimpleLine, Trash } from '@phosphor-icons/react';
import { DialogHuelle, ImmoButton, TextFeld } from '../../../../components/immobilien/ui';
import { supabase } from '../../../../lib/supabase/client';
import { BRIEF_KATEGORIEN, VERFUEGBARE_PLATZHALTER, briefVorlageHtml } from '../../../../lib/dokumente/briefVorlagen';
import { ladeVorlagenExtras, type VorlagenExtras } from '../../../../lib/schreiben/favoriten';
import { ZIELGRUPPEN } from '../../../../lib/schreiben/typen';
import { EINSCHREIBEN } from '../../../../lib/epost/epost';
import { SchalterZeile } from '../../../../components/immobilien/ui';
import { vorlageZuBrief } from '../../../../lib/dokumente/briefDaten';
import { ladeBriefkoepfe, type Briefkopf } from '../../../../lib/schreiben/gestaltung';
import { istAdmin } from '../../../../lib/schreiben/rechte';
import { ladeVersionen, versionZuEditor, type VorlagenVersion } from '../../../../lib/schreiben/vorlagenVersionen';
import { ClockCounterClockwise } from '@phosphor-icons/react';

const FELD = {
  width: '100%', padding: '10px 12px', borderRadius: 14, border: 'none', outline: 'none', resize: 'vertical' as const,
  background: 'var(--fill2)', color: 'var(--ink)', fontSize: 14, lineHeight: 1.5, fontFamily: 'inherit',
};
const LABEL = { fontSize: 12, fontWeight: 700, color: 'var(--ink3)' } as const;

export interface VorlageZumBearbeiten { id: string; name: string; kategorie: string; beschreibung: string | null; html_template: string }

export function BriefVorlageEditor({ vorlage, startKategorie, onSchliessen, onGespeichert }: {
  vorlage: VorlageZumBearbeiten | null;
  startKategorie?: string;
  onSchliessen: () => void;
  onGespeichert: (meldung: string) => void;
}) {
  const start = vorlage ? vorlageZuBrief(vorlage.html_template, {}) : { betreff: '', text: '{{empfaenger.briefanrede}}\n\n\n\nMit freundlichen Grüßen\n\nImmobilienmanagement Schwede' };
  const [name, setName] = useState(vorlage?.name ?? '');
  const [kategorie, setKategorie] = useState(vorlage?.kategorie ?? startKategorie ?? 'anschreiben');
  const [beschreibung, setBeschreibung] = useState(vorlage?.beschreibung ?? '');
  // #586: Zielgruppe, „immer zusätzlich per Post“, Begleittext – null solange die Spalten (Migration) fehlen
  const [extras, setExtras] = useState<VorlagenExtras | null>(null);
  useEffect(() => { void ladeVorlagenExtras(vorlage?.id ?? null).then(setExtras); }, [vorlage?.id]);
  const [koepfe, setKoepfe] = useState<Briefkopf[]>([]);
  useEffect(() => { ladeBriefkoepfe().then(setKoepfe, () => setKoepfe([])); }, []);
  // Etappe 4: ändern nur Admins (RLS); Vorgangsarten für die Wiedervorlage; frühere Fassungen
  const [darf, setDarf] = useState<boolean | null>(null);
  useEffect(() => { void istAdmin().then(setDarf, () => setDarf(false)); }, []);
  const [vorgangsarten, setVorgangsarten] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => { void supabase.from('vorgangsarten').select('id, name').order('name').then(({ data }) => setVorgangsarten((data ?? []) as { id: string; name: string }[])); }, []);
  const [versionen, setVersionen] = useState<VorlagenVersion[] | null>(null);
  useEffect(() => { if (vorlage) void ladeVersionen(vorlage.id).then(setVersionen); }, [vorlage?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [geladen, setGeladen] = useState<number | null>(null);
  const [betreff, setBetreff] = useState(start.betreff);
  const [text, setText] = useState(start.text);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const betreffAktiv = useRef(false);

  const einfuegen = (key: string) => einfuegenRoh(`{{${key}}}`);
  /** Text an der Cursorstelle einfügen (Etappe 5: Gerüste für Abschnitte und Berechnungen) */
  const einfuegenRoh = (marke: string) => {
    if (betreffAktiv.current) { setBetreff((b) => `${b}${b && !b.endsWith(' ') ? ' ' : ''}${marke}`); return; }
    const el = textRef.current;
    if (!el) { setText((t) => t + marke); return; }
    const von = el.selectionStart ?? text.length, bis = el.selectionEnd ?? von;
    const neu = text.slice(0, von) + marke + text.slice(bis);
    setText(neu);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(von + marke.length, von + marke.length); });
  };

  const [neuesFeld, setNeuesFeld] = useState('');
  /** {{eingabe.schluessel|Bezeichnung|typ}} – Typ aus der Bezeichnung geraten (Datum/Betrag/Text). */
  const eingabeEinfuegen = () => {
    const b = neuesFeld.trim();
    if (!b) return;
    const typ = /\?\s*$/.test(b) ? 'janein' : /datum|frist|termin|tag/i.test(b) ? 'datum' : /betrag|summe|euro|preis|miete/i.test(b) ? 'betrag' : 'text';
    const schluessel = b.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'feld';
    einfuegen(`eingabe.${schluessel}|${b}|${typ}`);
    setNeuesFeld('');
  };

  const fassungLaden = (v: VorlagenVersion) => {
    const f = versionZuEditor(v.daten);
    setName(f.name); setKategorie(f.kategorie); setBeschreibung(f.beschreibung); setBetreff(f.betreff); setText(f.text);
    setExtras((x) => (x ? { ...x, ...f.extras } : x));
    setGeladen(v.version);
  };

  const speichern = async () => {
    if (!darf) { setFehler('Vorlagen ändern dürfen nur Admins.'); return; }
    if (!name.trim()) { setFehler('Bitte einen Namen eingeben.'); return; }
    if (!betreff.trim()) { setFehler('Bitte einen Betreff eingeben.'); return; }
    setLaeuft(true); setFehler(null);
    const daten = { name: name.trim(), kategorie, beschreibung: beschreibung.trim() || null, html_template: briefVorlageHtml(betreff, text), aktiv: true, updated_at: new Date().toISOString() };
    const mitExtras = extras ? {
      ...daten, zielgruppe: extras.zielgruppe, immer_post: extras.immer_post, begleittext: extras.begleittext.trim() || null,
      ...(extras.briefkopf_id !== undefined ? { briefkopf_id: extras.briefkopf_id } : {}),
      ...(extras.schriftform !== undefined ? { schriftform: extras.schriftform, einschreiben: extras.einschreiben || null } : {}),
      ...(extras.wiedervorlage_tage !== undefined ? { wiedervorlage_tage: extras.wiedervorlage_tage || null, vorgangsart_id: extras.vorgangsart_id || null } : {}),
    } : daten;
    // .select: eine von der Zugriffsregel gefilterte Änderung liefert keinen Fehler, nur 0 Zeilen
    const { data: zeilen, error } = vorlage
      ? await supabase.from('dokument_vorlagen').update(mitExtras).eq('id', vorlage.id).select('id')
      : await supabase.from('dokument_vorlagen').insert({ ...mitExtras, ist_standard: false }).select('id');
    setLaeuft(false);
    if (error) { setFehler(error.message); return; }
    if (!zeilen?.length) { setFehler('Nicht gespeichert – Vorlagen ändern dürfen nur Admins.'); return; }
    onGespeichert(vorlage ? 'Vorlage gespeichert' : 'Vorlage angelegt');
  };

  const entfernen = async () => {
    if (!vorlage || !window.confirm(`Vorlage „${vorlage.name}“ entfernen?`)) return;
    setLaeuft(true);
    const { data: zeilen, error } = await supabase.from('dokument_vorlagen').update({ aktiv: false, updated_at: new Date().toISOString() }).eq('id', vorlage.id).select('id');
    setLaeuft(false);
    if (error) { setFehler(error.message); return; }
    if (!zeilen?.length) { setFehler('Nicht entfernt – Vorlagen ändern dürfen nur Admins.'); return; }
    onGespeichert('Vorlage entfernt');
  };

  return (
    <DialogHuelle titel={vorlage ? 'Vorlage bearbeiten' : 'Neue Brief-Vorlage'} untertitel="Platzhalter werden beim Brief mit den Daten des Empfängers gefüllt"
      icon={PencilSimpleLine} onSchliessen={onSchliessen} laeuft={laeuft} breite={960}
      fuss={
        <div className="flex w-full flex-wrap items-center" style={{ gap: 8 }}>
          {vorlage && darf && <ImmoButton art="bad" icon={Trash} onClick={() => void entfernen()} disabled={laeuft}>Entfernen</ImmoButton>}
          {darf === false && <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>Nur ansehen – Vorlagen ändern dürfen nur Admins.</span>}
          <span className="flex-1" />
          <ImmoButton onClick={onSchliessen} disabled={laeuft}>{darf ? 'Abbrechen' : 'Schließen'}</ImmoButton>
          {darf && <ImmoButton art="akzent" icon={FloppyDisk} onClick={() => void speichern()} disabled={laeuft}>{laeuft ? 'Speichert …' : 'Speichern'}</ImmoButton>}
        </div>
      }>
      <fieldset disabled={!darf} style={{ border: 'none', margin: 0, padding: 0, minWidth: 0, display: 'contents' }}>
      <div className="immo-scroll grid overflow-y-auto" style={{ gap: 16, gridTemplateColumns: 'minmax(0,1fr) minmax(220px, 260px)' }}>
        <div className="flex flex-col" style={{ gap: 12 }}>
          <TextFeld label="Name" wert={name} onChange={setName} placeholder="z. B. Ankündigung Handwerkertermin" />
          <label className="flex flex-col" style={{ gap: 6 }}>
            <span style={LABEL}>Typ</span>
            <select value={kategorie} onChange={(e) => setKategorie(e.target.value)} style={{ ...FELD, height: 44, padding: '0 12px', cursor: 'pointer' }}>
              {BRIEF_KATEGORIEN.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          <TextFeld label="Beschreibung (optional)" wert={beschreibung} onChange={setBeschreibung} placeholder="Wofür ist die Vorlage gedacht?" />
          {extras && (
            <>
              <label className="flex flex-col" style={{ gap: 6 }}>
                <span style={LABEL}>Zielgruppe (Filter im Schreiben-Assistenten)</span>
                <select value={extras.zielgruppe ?? ''} onChange={(e) => setExtras({ ...extras, zielgruppe: e.target.value || null })} style={{ ...FELD, height: 44, padding: '0 12px', cursor: 'pointer' }}>
                  <option value="">Keine</option>
                  {ZIELGRUPPEN.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              {extras.briefkopf_id !== undefined && koepfe.filter((k) => k.aktiv).length > 1 && (
                <label className="flex flex-col" style={{ gap: 6 }}>
                  <span style={LABEL}>Briefkopf</span>
                  <select value={extras.briefkopf_id ?? ''} onChange={(e) => setExtras({ ...extras, briefkopf_id: e.target.value || null })} style={{ ...FELD, height: 44, padding: '0 12px', cursor: 'pointer' }}>
                    <option value="">Standard</option>
                    {koepfe.filter((k) => k.aktiv).map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                  </select>
                </label>
              )}
              <SchalterZeile an={extras.immer_post} onChange={(v) => setExtras({ ...extras, immer_post: v })} label="Immer zusätzlich per Post"
                zusatz="Für Schreiben, deren Zugang nachweisbar sein muss (z. B. Mieterhöhung, Abmahnung)" />
              {extras.schriftform !== undefined && (
                <>
                  <SchalterZeile an={extras.schriftform} onChange={(v) => setExtras({ ...extras, schriftform: v })} label="Schriftform (Original mit Unterschrift)"
                    zusatz="Z. B. Kündigung: Original aus dem Druckstapel, von Hand unterschrieben. E-Mail und Portal nur als Vorab-Info." />
                  <label className="flex flex-col" style={{ gap: 6 }}>
                    <span style={LABEL}>Einschreiben (Voreinstellung)</span>
                    <select value={extras.einschreiben ?? ''} onChange={(e) => setExtras({ ...extras, einschreiben: e.target.value || null })} style={{ ...FELD, height: 44, padding: '0 12px', cursor: 'pointer' }}>
                      <option value="">Normaler Brief</option>
                      {EINSCHREIBEN.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </label>
                </>
              )}
              {extras.wiedervorlage_tage !== undefined && (
                <div className="flex flex-wrap" style={{ gap: 10 }}>
                  <label className="flex flex-col" style={{ gap: 6, flex: '0 1 200px' }}>
                    <span style={LABEL}>Wiedervorlage nach … Tagen</span>
                    <input type="number" min={1} max={365} value={extras.wiedervorlage_tage ?? ''} placeholder="keine"
                      onChange={(e) => { const n = Math.round(Number(e.target.value)); setExtras({ ...extras, wiedervorlage_tage: e.target.value && n >= 1 && n <= 365 ? n : null }); }}
                      style={{ ...FELD, height: 44 }} />
                  </label>
                  <label className="flex flex-col" style={{ gap: 6, flex: '1 1 220px' }}>
                    <span style={LABEL}>Wiedervorlage als Vorgang</span>
                    <select value={extras.vorgangsart_id ?? ''} onChange={(e) => setExtras({ ...extras, vorgangsart_id: e.target.value || null })} style={{ ...FELD, height: 44, padding: '0 12px', cursor: 'pointer' }}>
                      <option value="">Normales Ticket</option>
                      {vorgangsarten.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </label>
                  <span style={{ fontSize: 12, color: 'var(--ink3)', flexBasis: '100%' }}>Gibt es ein Eingabefeld „Frist“, gilt der Tag danach. Im Assistenten änderbar.</span>
                </div>
              )}
              <label className="flex flex-col" style={{ gap: 6 }}>
                <span style={LABEL}>Begleittext für die E-Mail (optional)</span>
                <textarea rows={3} value={extras.begleittext} onChange={(e) => setExtras({ ...extras, begleittext: e.target.value })}
                  placeholder={'{{empfaenger.briefanrede}}\n\nanbei erhalten Sie unser Schreiben als PDF.\n\nMit freundlichen Grüßen\nImmobilienmanagement Schwede'} style={FELD} />
              </label>
            </>
          )}
          <label className="flex flex-col" style={{ gap: 6 }}>
            <span style={LABEL}>Betreff</span>
            <input value={betreff} onChange={(e) => setBetreff(e.target.value)} onFocus={() => { betreffAktiv.current = true; }}
              placeholder="z. B. Handwerkertermin in Ihrer Wohnung {{objekt.strasse}} {{objekt.hausnummer}}" style={{ ...FELD, height: 44 }} />
          </label>
          <label className="flex flex-col" style={{ gap: 6 }}>
            <span style={LABEL}>Text</span>
            <textarea ref={textRef} rows={16} value={text} onChange={(e) => setText(e.target.value)} onFocus={() => { betreffAktiv.current = false; }} style={FELD} />
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Leerzeile = neuer Absatz. Briefpapier, Anschrift und Datum setzt die App selbst.</span>
          </label>
          {fehler && <span role="alert" style={{ fontSize: 12.5, color: 'var(--bad)' }}>{fehler}</span>}
        </div>
        <aside className="flex flex-col" style={{ gap: 10 }} aria-label="Platzhalter">
          {!!versionen?.length && (
            <details className="flex flex-col" style={{ padding: 8, borderRadius: 12, background: 'var(--fill)' }}>
              <summary className="flex items-center cursor-pointer" style={{ gap: 6, fontSize: 12, fontWeight: 800 }}>
                <ClockCounterClockwise size={14} weight="bold" />Frühere Fassungen ({versionen.length})
              </summary>
              <div className="flex flex-col" style={{ gap: 4, marginTop: 6 }}>
                {versionen.map((v) => (
                  <div key={v.id} className="flex items-center" style={{ gap: 6, fontSize: 11.5 }}>
                    <span className="min-w-0 flex-1" style={{ color: 'var(--ink2)' }}>
                      Fassung {v.version} · bis {new Date(v.gueltigBis).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      {v.ersetztVon ? ` · ersetzt von ${v.ersetztVon}` : ''}
                    </span>
                    {darf && (
                      <button type="button" onClick={() => fassungLaden(v)} className="cursor-pointer"
                        style={{ height: 28, padding: '0 8px', borderRadius: 8, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontWeight: 800, fontSize: 11.5 }}>
                        Laden
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {geladen !== null && <span style={{ fontSize: 11.5, color: 'var(--warn)', marginTop: 6 }}>Fassung {geladen} geladen – erst „Speichern“ macht sie wieder aktuell.</span>}
            </details>
          )}
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Klick fügt den Platzhalter dort ein, wo der Cursor steht.</span>
          <div className="flex flex-col" style={{ gap: 6, padding: 8, borderRadius: 12, background: 'var(--acc-soft)' }}>
            <span style={{ fontSize: 12, fontWeight: 800 }}>Eingabefeld einfügen</span>
            <span style={{ fontSize: 11, color: 'var(--ink3)' }}>wird beim Schreiben ausgefüllt (Datum, Betrag, Frist …)</span>
            <div className="flex" style={{ gap: 6 }}>
              <input value={neuesFeld} onChange={(e) => setNeuesFeld(e.target.value)} placeholder="z. B. Frist" aria-label="Bezeichnung des Eingabefelds"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); eingabeEinfuegen(); } }}
                style={{ ...FELD, height: 36, padding: '0 10px', minWidth: 0, flex: 1 }} />
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={eingabeEinfuegen} disabled={!neuesFeld.trim()} className="cursor-pointer"
                style={{ height: 36, padding: '0 12px', borderRadius: 10, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontWeight: 800, fontSize: 12 }}>Einfügen</button>
            </div>
          </div>
          <div className="flex flex-col" style={{ gap: 6, padding: 8, borderRadius: 12, background: 'var(--fill)' }}>
            <span style={{ fontSize: 12, fontWeight: 800 }}>Bausteine</span>
            <span style={{ fontSize: 11, color: 'var(--ink3)' }}>
              Abschnitt erscheint nur, wenn die Bedingung zutrifft (= / != Wert, {'{{#sonst}}'} für den anderen Fall). Berechnung mit + − * / und Klammern;
              Datum + 14 = 14 Tage später. Ankreuzfeld: im Schreiben ein Häkchen, im Brief ☒ oder ☐.
            </span>
            <div className="flex flex-wrap" style={{ gap: 6 }}>
              {[
                ['Bedingter Abschnitt', '{{#wenn eingabe.frage|Soll der Absatz rein?|janein}}\nText, der nur bei „Ja“ erscheint.\n{{/wenn}}'],
                ['Auswahl-Abschnitt', '{{#wenn eingabe.stufe|Mahnstufe|auswahl:1/2/3 = 3}}\nText für Stufe 3.\n{{#sonst}}\nText für die anderen Stufen.\n{{/wenn}}'],
                // #589: Kästchen zum Ankreuzen; das Datum ist nur Pflicht, wenn angekreuzt
                ['Ankreuzfeld', '{{eingabe.kenntnis|Zur Kenntnis nehmen|ankreuzen}} Bitte zur Kenntnis nehmen'],
                ['Ankreuzfeld mit Datum', '{{eingabe.zurueck|Unterschreiben und zurücksenden|ankreuzen}} Bitte unterschreiben und bis zum {{#wenn eingabe.zurueck = Ja}}{{eingabe.zurueck_bis|Rücksendung bis|datum}}{{#sonst}}__________{{/wenn}} zurücksenden'],
                ['Berechnung', '{{= vertrag.kaltmiete + eingabe.erhoehung|Erhöhung|betrag}}'],
                ['Datum + Tage', '{{= datum.heute + 14}}'],
              ].map(([l, g]) => (
                <button key={l} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => einfuegenRoh(g)} className="cursor-pointer"
                  style={{ height: 32, padding: '0 10px', borderRadius: 10, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontWeight: 800, fontSize: 11.5 }}>{l}</button>
              ))}
            </div>
          </div>
          {VERFUEGBARE_PLATZHALTER.map((g) => (
            <div key={g.gruppe} className="flex flex-col" style={{ gap: 2 }}>
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{g.gruppe}</span>
              {g.eintraege.map((p) => (
                <button key={p.key} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => einfuegen(p.key)}
                  className="immo-hover-flaeche flex flex-col text-left cursor-pointer"
                  style={{ minHeight: 40, padding: '4px 8px', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink)', justifyContent: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{p.label}</span>
                  <code style={{ fontSize: 11, color: 'var(--ok)', fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace" }}>{`{{${p.key}}}`}</code>
                </button>
              ))}
            </div>
          ))}
        </aside>
      </div>
      </fieldset>
    </DialogHuelle>
  );
}
